/**
 * Krok UXR3 — „CO SE NAPOSLEDY STALO" ZE SKUTECNE HISTORIE ZAKAZKY.
 *
 * Nalez R.B. 2. 10. 2026 23:34: „»Zakázky v pohybu« jsou tri stejne radky
 * »Příprava dnes 12:23« s sedou ikonou." Priciny byly dve a obe tenhle modul
 * resi:
 *  - radek psal FAZI (`stage`), ne to, co se stalo; tri zakazky v teze fazi
 *    daly tri stejne radky,
 *  - druhy sloupec bral `description ?? nextTaskTitle`, coz je POPIS zakazky
 *    (u demo zakazek stejny nebo prazdny), ne udalost.
 *
 * Zadani UXR3 bod 4 proto zada „co se naposledy stalo — text ze SKUTECNE
 * historie zakazky". Zdroj je `GET /v1/cases/:id/timeline` (projekce
 * `case_events` + zpravy + dokumenty, `packages/case/src/internal/service.ts`
 * `getTimeline`), tedy tataz historie, jakou detail zakazky kresli v
 * „Poslední dění". Jeden vypocet pro obe místa.
 *
 * ---------------------------------------------------------------------------
 * ROZHODNUTI UXR3-HISTORIE — co se pocita za „deni" a co je sum
 *
 * Projekce obsahuje i udalosti, ktere jsou zapisem PRACE STROJE, ne tim, co se
 * stalo zakazce: `FACT_VERIFIED` (desitky na jeden beh), `AI_ROLE_CALL`,
 * `AI_OPPONENT_VERDICT`, `AI_CONSISTENCY_CHECK`. Kdyby se braly, posledni
 * udalosti kazde proverené zakazky by znela „oveřen jeden fakt" — pravdive,
 * ale makleri to nerekne nic. Bezi uvnitr auditu, jehoz zacatek i konec
 * historie nese zvlast (`AUDIT_RUN_STARTED`/`FINISHED`), takze se nic
 * neztraci. Vycet je `NOISE_KINDS` niz a hlida ho test.
 *
 * Neznamy druh udalosti NEVYMYSLIME: veta vyjde `null` a obrazovka
 * sloupec nechá prazdny. Lepsi prazdno nez veta, kterou data nepodporuji.
 * ---------------------------------------------------------------------------
 */

import { t } from '@reos/i18n';
import { stageLabel } from './case-stages.js';
import type { CaseTimelineItem } from './cases-api.js';

const cs = { locale: 'cs' } as const;

/**
 * Druhy udalosti, ktere jsou zapisem prace stroje uvnitr auditu — viz
 * ROZHODNUTI UXR3-HISTORIE v hlavicce souboru.
 */
export const NOISE_KINDS: readonly string[] = [
  'FACT_VERIFIED',
  'AI_ROLE_CALL',
  'AI_OPPONENT_VERDICT',
  'AI_CONSISTENCY_CHECK',
];

export interface CaseEventLine {
  readonly id: string;
  /** Jedna lidska veta: co se stalo. */
  readonly text: string;
  /** Kdy (ISO) — radek si ji formatuje sam (relativni cas). */
  readonly at: string;
  readonly kind: string;
}

/**
 * Krok ZAK1 bod 2 — STAV PREDVYPLNENI Z OTEVRENYCH DAT.
 *
 * Zapisuje ho worker a aplikacni vrstva do casove osy zakazky jako
 * `kind: 'system'` s `change: 'property_prefill'` (vlastnik tvaru je
 * `@reos/case` `appendPropertyPrefillEvent`). Detail zakazky z toho kresli
 * hlasku „Načítám údaje o nemovitosti…" a datum nacteni — jina cesta ke stavu
 * neexistuje, takze tenhle prevod je jedine misto, kde se cte.
 */
export const PROPERTY_PREFILL_CHANGE = 'property_prefill';

/**
 * Krok SCHV1 bod 3 — DOKLAD O ROZHODNUTI MAJITELE V HISTORII ZAKAZKY.
 *
 * Zapisuje ho @reos/crm pres port (`appendOwnerApprovalEvent` v @reos/case)
 * jako `kind: 'system'` s `change: 'owner_approval'` — tentyz vzor jako
 * predvyplneni vys (novy druh `kind` ma v DB CHECK, viz komentar u zapisu).
 * Cas nese radek sam, IP a text pripominek payload; do vety se NEDAVAJI
 * (IP neni pro maklere informace a pripominky ukazuje karta nabidky).
 */
export const OWNER_APPROVAL_CHANGE = 'owner_approval';

export type PrefillState = 'queued' | 'done';

export interface PrefillStatus {
  readonly state: PrefillState;
  /** Kdy zapis vznikl (ISO) — „datum nacteni" u udaju z registru. */
  readonly at: string;
  /** Kolik poli se vyplnilo samo; `null` u zarazeni. */
  readonly facts: number | null;
}

function num(payload: unknown, key: string): number | null {
  if (payload === null || typeof payload !== 'object') return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function prefillStateOf(item: CaseTimelineItem): PrefillState {
  return str(item.payload, 'state') === 'queued' ? 'queued' : 'done';
}

/**
 * Posledni stav predvyplneni z historie zakazky, nebo `null` kdyz zakazka
 * zadne nema (starsi zakazky, zakazka bez nemovitosti). Historie prichazi
 * v poradi od nejnovejsi (`getTimeline`), ale na poradi se NESPOLEHAME —
 * rozhoduje `createdAt`, aby prehozene poradi neudelalo z hotoveho nacteni
 * vecne „Načítám…".
 */
export function prefillStatus(items: readonly CaseTimelineItem[]): PrefillStatus | null {
  let best: PrefillStatus | null = null;
  for (const item of items) {
    if (item.kind !== 'system' || str(item.payload, 'change') !== PROPERTY_PREFILL_CHANGE) continue;
    const candidate: PrefillStatus = {
      state: prefillStateOf(item),
      at: item.createdAt,
      facts: num(item.payload, 'facts'),
    };
    if (best === null || candidate.at >= best.at) best = candidate;
  }
  return best;
}

function str(payload: unknown, key: string): string | null {
  if (payload === null || typeof payload !== 'object') return null;
  const value = (payload as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Lidska veta k jedne udalosti, nebo `null` u druhu, ktery neumime
 * pojmenovat. Payload se nikdy nevypisuje surovy (pravidlo „zadny technicky
 * text v UI", hlida `test/e2e-preview/helpers.ts`).
 */
export function caseEventSentence(item: CaseTimelineItem): string | null {
  switch (item.kind) {
    case 'case_created':
      return t('webAdmin.caseHistory.caseCreated', cs);
    /*
     * Zakazky, ktere vznikly PREVODEM z hotoveho prověření
     * (`scripts/db/import-r3-audits.mjs`) nemaji `case_created`, ale tenhle
     * druh — a na preview je to u demo kancelare JEDINA udalost, kterou
     * historie ma (zjisteno pri zivem overeni UXR3). Pro maklere je to porad
     * zalozeni zakazky, jen jinou cestou; bez teto vetvy by sloupec „co se
     * naposledy stalo" u vsech demo zakazek zustal prazdny.
     */
    case 'CASE_CREATED_FROM_AUDIT_BACKFILL':
      return t('webAdmin.caseHistory.caseImported', cs);
    case 'stage_changed': {
      const to = str(item.payload, 'to');
      // Bez cilove faze by veta rekla „posunuto do undefined".
      if (to === null) return null;
      return t('webAdmin.caseHistory.stageChanged', { ...cs, values: { phase: stageLabel(to) } });
    }
    case 'AUDIT_RUN_STARTED':
      return t('webAdmin.caseHistory.auditStarted', cs);
    case 'AUDIT_RUN_FINISHED':
      return t('webAdmin.caseHistory.auditFinished', cs);
    // Krok SIGN1 bod 3: smlouvu podepsaly vsechny strany (zapis dela
    // @reos/documents po zapecetění relace).
    case 'CONTRACT_SIGNED':
      return t('webAdmin.caseHistory.contractSigned', cs);
    /*
     * Krok P8F6 bod 3 (nalez P8-N12): makler potvrdil nabor na miste. Do
     * P8F6 historie zakazky po naboru koncila vetou „Zakázka založena",
     * takze nebylo videt, ze nabor vubec byl.
     */
    case 'FIELD_INTAKE_CONFIRMED':
      return t('webAdmin.caseHistory.intakeConfirmed', cs);
    case 'GATE_CLOSED':
      return t('webAdmin.caseHistory.areaVerified', cs);
    case 'QUESTION_ANSWERED':
      return t('webAdmin.caseHistory.questionAnswered', cs);
    case 'AUDIT_EXPORT_READY':
      return t('webAdmin.caseHistory.reportReady', cs);
    case 'AUDIT_EXPORT_FAILED':
      return t('webAdmin.caseHistory.reportFailed', cs);
    case 'note': {
      const text = str(item.payload, 'text');
      return text === null
        ? t('webAdmin.caseHistory.note', cs)
        : t('webAdmin.caseHistory.noteWithText', { ...cs, values: { text } });
    }
    case 'message': {
      const subject = str(item.payload, 'subject');
      const incoming = str(item.payload, 'direction') === 'inbound';
      if (subject === null) {
        return incoming ? t('webAdmin.caseHistory.messageIn', cs) : t('webAdmin.caseHistory.messageOut', cs);
      }
      return incoming
        ? t('webAdmin.caseHistory.messageInWithSubject', { ...cs, values: { subject } })
        : t('webAdmin.caseHistory.messageOutWithSubject', { ...cs, values: { subject } });
    }
    case 'document': {
      const title = str(item.payload, 'title');
      return title === null
        ? t('webAdmin.caseHistory.document', cs)
        : t('webAdmin.caseHistory.documentWithTitle', { ...cs, values: { title } });
    }
    case 'task': {
      const title = str(item.payload, 'title');
      const status = str(item.payload, 'status');
      if (title !== null) return t('webAdmin.caseHistory.taskAdded', { ...cs, values: { title } });
      if (status === 'done') return t('webAdmin.caseHistory.taskDone', cs);
      return t('webAdmin.caseHistory.taskChanged', cs);
    }
    case 'promise': {
      const title = str(item.payload, 'title');
      return title === null
        ? t('webAdmin.caseHistory.promiseChanged', cs)
        : t('webAdmin.caseHistory.promiseAdded', { ...cs, values: { title } });
    }
    case 'control':
      return t('webAdmin.caseHistory.control', cs);
    case 'system': {
      // `system` nese v payloadu `action`; dva z nich jsou pro maklere
      // smysluplne (pribyl/odesel clovek), zbytek je provozni zapis.
      const action = str(item.payload, 'action');
      if (action === 'participant_added' || action === 'user_participant_added') {
        return t('webAdmin.caseHistory.participantAdded', cs);
      }
      if (action === 'user_participant_removed') return t('webAdmin.caseHistory.participantRemoved', cs);
      /*
       * Krok ZAK1 bod 2: predvyplneni z otevrenych dat. Je to zapis, ktery
       * makler CHCE videt („vyplnilo se samo X polí"), proto na rozdil od
       * ostatnich provoznich `system` zapisu vetu ma.
       */
      /*
       * Krok SCHV1 bod 3: souhlas majitele je presne ten zapis, ktery
       * makler v historii hleda („uz to schvalil?"), proto vetu ma.
       */
      if (str(item.payload, 'change') === OWNER_APPROVAL_CHANGE) {
        const decision = str(item.payload, 'decision');
        if (decision === 'approve') return t('webAdmin.caseHistory.ownerApprovalApproved', cs);
        if (decision === 'objections') return t('webAdmin.caseHistory.ownerApprovalObjections', cs);
        return t('webAdmin.caseHistory.ownerApprovalRequested', cs);
      }
      if (str(item.payload, 'change') === PROPERTY_PREFILL_CHANGE) {
        const state = prefillStateOf(item);
        if (state === 'queued') return t('webAdmin.caseHistory.prefillQueued', cs);
        const facts = num(item.payload, 'facts');
        return facts === null || facts === 0
          ? t('webAdmin.caseHistory.prefillNothing', cs)
          : t('webAdmin.caseHistory.prefillDone', { ...cs, values: { count: facts } });
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Poslednich `limit` udalosti, nejnovejsi prvni — „Poslední dění" v detailu
 * zakazky (od kroku UXV4a jedine misto, kde se historie cte — Dnesek ma
 * podle predlohy v4 v radku POSTUP, ne „co se naposledy stalo").
 */
export function caseEventLines(
  timeline: readonly CaseTimelineItem[],
  limit: number,
): readonly CaseEventLine[] {
  const lines: CaseEventLine[] = [];
  // Projekce prichazi vzestupne podle `createdAt` (viz `getTimeline`), ale
  // spolehat se na poradi ciziho endpointu by byla tichá zavislost — radime.
  const ordered = [...timeline].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  for (const item of ordered) {
    if (NOISE_KINDS.includes(item.kind)) continue;
    const text = caseEventSentence(item);
    if (text === null) continue;
    lines.push({ id: item.id, text, at: item.createdAt, kind: item.kind });
    if (lines.length >= limit) break;
  }
  return lines;
}
